// Geometry for the hill chart, shared by the SVG renderer and the drag handler.
// A scope's progress is a 0-1 fraction: 0 is the foot of the "figuring things
// out" slope, 0.5 is the peak, and 1 is the bottom of "making it happen".

export const HILL_COLORS = {
  done: '#22c55e',
  making: '#F59E0B',
  figuring: '#06B6D4',
  notStarted: '#737373',
};

/**
 * Colour a dot by which stretch of the hill it sits on.
 */
export const zoneColor = (fraction) => {
  if (fraction >= 0.96) return HILL_COLORS.done;
  if (fraction >= 0.55) return HILL_COLORS.making;
  if (fraction >= 0.07) return HILL_COLORS.figuring;
  return HILL_COLORS.notStarted;
};

/**
 * Height of the hill at a given progress fraction.
 */
export const hillY = (fraction, baseline, amplitude) =>
  baseline - Math.sin(fraction * Math.PI) * amplitude;

/**
 * SVG path describing the whole hill.
 */
export const buildHillPath = (width, baseline, amplitude, steps = 120) => {
  const points = [];
  for (let i = 0; i <= steps; i += 1) {
    const fraction = i / steps;
    points.push(`${(fraction * width).toFixed(1)},${hillY(fraction, baseline, amplitude).toFixed(1)}`);
  }
  return `M ${points.join(' L ')}`;
};

// Scopes sharing a status land on the same point, so a cluster is fanned out
// along the hill first and only stacked upward once a row is full. Spreading
// sideways keeps dots on the curve instead of building a tower that would
// escape the top of the chart.
const CLUSTER_COLS = 5;
const CLUSTER_COL_SPACING = 0.035;
// However far a cluster fans out, it must still read as "these scopes are at
// this point on the hill", so the spread is capped and dots pack tighter
// instead of sprawling across the chart.
const CLUSTER_MAX_SPREAD = 0.16;
// Near either foot of the hill the curve is steep, so fanning a cluster out
// along it would lift the outer dots noticeably up the slope and read as
// progress. Clusters this close to an end stack straight up from a shared
// height instead, keeping every dot at the same point on the hill.
const EDGE_ZONE = 0.12;

/**
 * Place each scope as a dot, fanning out any that land on the same spot so they
 * stay individually visible.
 */
export const layoutDots = (scopes, { width, baseline, amplitude, radius }) => {
  const buckets = new Map();

  scopes.forEach((scope) => {
    const fraction = typeof scope.position === 'number' ? scope.position : 0.04;
    const bucket = Math.round(fraction * 24);
    if (!buckets.has(bucket)) buckets.set(bucket, []);
    buckets.get(bucket).push({ ...scope, fraction });
  });

  const rowSpacing = radius * 2 + 3;
  const minY = radius + 2;
  const dots = [];

  buckets.forEach((group) => {
    const count = group.length;
    const anchorFraction = group[0].fraction;

    // Rows can only use the headroom between the curve and the top of the
    // chart, so once that runs out the cluster grows sideways instead.
    const anchorY = hillY(anchorFraction, baseline, amplitude);
    const maxRows = Math.max(1, Math.floor((anchorY - minY) / rowSpacing) + 1);

    let columns = Math.min(count, CLUSTER_COLS);
    if (Math.ceil(count / columns) > maxRows) {
      columns = Math.ceil(count / maxRows);
    }
    const spacing = columns > 1
      ? Math.min(CLUSTER_COL_SPACING, CLUSTER_MAX_SPREAD / (columns - 1))
      : 0;

    // Shift a cluster sitting near either end inward as a whole, so its dots
    // keep even spacing instead of piling up against the edge of the chart.
    const halfSpread = ((columns - 1) / 2) * spacing;
    const edge = radius / width;
    const center = halfSpread * 2 > 1 - edge * 2
      ? 0.5
      : Math.min(1 - edge - halfSpread, Math.max(edge + halfSpread, anchorFraction));

    // A cluster near an end shares one anchor height so its dots line up
    // vertically, rather than climbing the steep slope as they fan out.
    const nearEdge = anchorFraction <= EDGE_ZONE || anchorFraction >= 1 - EDGE_ZONE;

    group.forEach((scope, index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const offset = (column - (columns - 1) / 2) * spacing;
      const fraction = Math.min(1, Math.max(0, center + offset));

      // Each column has its own headroom, since fanning out moves the outer
      // columns further up or down the slope. Rows compress to fit rather than
      // piling up at the top of the chart. Edge clusters skip this and share the
      // anchor height so the whole group stays level.
      const columnAnchorY = nearEdge ? anchorY : hillY(fraction, baseline, amplitude);
      const rowsInColumn = Math.floor((count - 1 - column) / columns) + 1;
      const step = rowsInColumn > 1
        ? Math.min(rowSpacing, (columnAnchorY - minY) / (rowsInColumn - 1))
        : 0;

      dots.push({
        ...scope,
        x: fraction * width,
        y: columnAnchorY - row * step,
      });
    });
  });

  return dots;
};

// Rough width of one character of the label font, used to predict overlaps
// without measuring text in the DOM.
const LABEL_CHAR_W = 4.7;
const LABEL_GAP = 11;

/**
 * Work out where each dot's label goes. Labels trail behind the direction of
 * travel, then any that would collide with another label or run behind a dot
 * are lifted clear, so every one stays readable even when scopes bunch up
 * around the peak.
 */
export const layoutLabels = (dots, {
  radius,
  getText,
  offset = 8,
  minY = 12,
  maxY = Infinity,
  obstacleDots = dots,
}) => {
  // Dots are fixed, so they act as obstacles the labels must avoid.
  const obstacles = obstacleDots.map(dot => ({
    left: dot.x - radius,
    right: dot.x + radius,
    y: dot.y,
    gap: radius + 4,
  }));

  const items = dots
    .map((dot) => {
      const pastPeak = dot.fraction > 0.5;
      const text = getText(dot);
      const anchorX = pastPeak ? dot.x - radius - offset : dot.x + radius + offset;
      const textWidth = text.length * LABEL_CHAR_W;
      return {
        id: dot.id,
        text,
        pastPeak,
        x: anchorX,
        y: dot.y + 4,
        left: pastPeak ? anchorX - textWidth : anchorX,
        right: pastPeak ? anchorX : anchorX + textWidth,
        gap: LABEL_GAP,
      };
    })
    .sort((a, b) => a.y - b.y);

  const placed = [];
  items.forEach((item) => {
    const collidesWith = (other) => other.right > item.left
      && other.left < item.right
      && Math.abs(other.y - item.y) < Math.max(other.gap, LABEL_GAP);

    const overlaps = () => placed.some(collidesWith)
      // A label never collides with the dot it belongs to, since it is offset
      // beyond that dot's radius.
      || obstacles.some(collidesWith);

    const naturalY = item.y;
    const seek = (step, bound) => {
      item.y = naturalY;
      while (overlaps()) {
        const next = item.y + step;
        if (step < 0 ? next < minY : next > bound) return false;
        item.y = next;
      }
      return true;
    };

    // Prefer the open space above the hill. Around the peak that space runs out,
    // so fall back to the empty area under the curve before giving up.
    if (!seek(-LABEL_GAP, minY) && !seek(LABEL_GAP, maxY)) {
      item.y = naturalY;
    }

    placed.push(item);
  });

  return new Map(placed.map(item => [item.id, item]));
};
